import type { ManagementDashboardStatusIdApi } from "../typings/management-dashboard.types";
import type { ManagementDisplayUnit } from "../typings/management-display-unit";
import type { ManagementDashboardViewModel } from "../utils/map-management-dashboard-response";

export type ManagementDonutDisplay = {
  percentOrValue: number;
  remainder: number;
  centerLabel: string;
  valueSuffix?: string;
};

export type ManagementStatusDonutSegment = {
  id: ManagementDashboardStatusIdApi;
  value: number;
};

export type ManagementStatusDonutDisplay = {
  centerLabel: string;
  valueSuffix?: string;
  segments: ManagementStatusDonutSegment[];
};

const STATUS_DONUT_SEGMENT_IDS: ManagementDashboardStatusIdApi[] = [
  "office",
  "mobileWork",
  "notSpecified"
];

export function getOccupancyDonutDisplay(
  dashboard: Pick<
    ManagementDashboardViewModel,
    "occupancyTodayPercent" | "occupiedSeats" | "freeSeats"
  >,
  unit: ManagementDisplayUnit
): ManagementDonutDisplay {
  if (unit === "percent") {
    return {
      percentOrValue: dashboard.occupancyTodayPercent,
      remainder: Math.max(0, 100 - dashboard.occupancyTodayPercent),
      centerLabel: `${dashboard.occupancyTodayPercent}%`,
      valueSuffix: "%"
    };
  }

  return {
    percentOrValue: dashboard.occupiedSeats,
    remainder: dashboard.freeSeats,
    centerLabel: String(dashboard.occupiedSeats)
  };
}

function statusBreakdownValue(
  dashboard: Pick<ManagementDashboardViewModel, "totalUsers" | "inOfficeCount" | "statusBreakdown">,
  id: ManagementDashboardStatusIdApi,
  unit: ManagementDisplayUnit
): number {
  const row = dashboard.statusBreakdown.find(entry => entry.id === id);
  if (unit === "percent") {
    if (row) {
      return row.percent;
    }
    if (id === "office") {
      return dashboard.totalUsers > 0
        ? Math.round((dashboard.inOfficeCount / dashboard.totalUsers) * 100)
        : 0;
    }
    return 0;
  }
  if (row) {
    return row.count;
  }
  return id === "office" ? dashboard.inOfficeCount : 0;
}

export function getStatusDonutDisplay(
  dashboard: Pick<ManagementDashboardViewModel, "totalUsers" | "inOfficeCount" | "statusBreakdown">,
  unit: ManagementDisplayUnit
): ManagementStatusDonutDisplay {
  const segments = STATUS_DONUT_SEGMENT_IDS.map(id => ({
    id,
    value: statusBreakdownValue(dashboard, id, unit)
  }));
  const officeValue = segments.find(segment => segment.id === "office")?.value ?? 0;

  if (unit === "percent") {
    return {
      centerLabel: `${officeValue}%`,
      valueSuffix: "%",
      segments
    };
  }

  return {
    centerLabel: String(officeValue),
    segments
  };
}

export function formatStatusBreakdownValue(
  row: { percent: number; count: number },
  unit: ManagementDisplayUnit
): string {
  return unit === "percent" ? `${row.percent}%` : String(row.count);
}

export function formatLocationRankingValue(
  row: { occupancyPercent: number; occupiedSeats: number },
  unit: ManagementDisplayUnit
): string {
  return unit === "percent" ? `${row.occupancyPercent}%` : String(row.occupiedSeats);
}

export function formatManagementChartTooltipValue(
  value: unknown,
  unit: ManagementDisplayUnit
): string {
  const display = typeof value === "number" || typeof value === "string" ? String(value) : "";
  return unit === "percent" ? `${display}%` : display;
}

export function getManagementPeriodChartYAxisProps(unit: ManagementDisplayUnit): {
  domain: [number, number] | [number, "auto"];
  allowDecimals: boolean;
  /** Prefer tickFormatter over `unit` — Recharts appends `unit` after the tooltip formatter (e.g. `42%%`). */
  tickFormatter?: (value: number) => string;
} {
  if (unit === "percent") {
    return {
      domain: [0, 100],
      allowDecimals: false,
      tickFormatter: (value: number) => `${value}%`
    };
  }

  return { domain: [0, "auto"], allowDecimals: false };
}

export function getOccupancyPeriodDataKey(
  unit: ManagementDisplayUnit
): "occupancyPercent" | "occupiedSeats" {
  return unit === "percent" ? "occupancyPercent" : "occupiedSeats";
}

export function getStatusPeriodDataKeys(unit: ManagementDisplayUnit): {
  office: "officePercent" | "officeCount";
  mobileWork: "mobileWorkPercent" | "mobileWorkCount";
  notSpecified: "notSpecifiedPercent" | "notSpecifiedCount";
} {
  if (unit === "percent") {
    return {
      office: "officePercent",
      mobileWork: "mobileWorkPercent",
      notSpecified: "notSpecifiedPercent"
    };
  }

  return {
    office: "officeCount",
    mobileWork: "mobileWorkCount",
    notSpecified: "notSpecifiedCount"
  };
}
